// Client for the Rust data API, reached through the same-origin BFF proxy
// (/api/data/*). The browser carries only the HttpOnly camp_session cookie;
// the proxy injects the API token server-side. This is the sole data backend.

const BASE = '/api/data'

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}/${path}`, {
    credentials: 'include',
    ...init,
  })
  if (!res.ok) {
    let detail = ''
    try {
      detail = JSON.stringify(await res.json())
    } catch {
      /* ignore */
    }
    throw new Error(`API ${res.status} ${path} ${detail}`)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
})

/** Bulk initial load — the fetchAllData replacement. */
export function apiBootstrap(organizationId: string | null): Promise<Record<string, unknown[]>> {
  const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : ''
  return req(`bootstrap${q}`)
}

export function apiInsert<T = Record<string, unknown>>(table: string, row: unknown): Promise<T> {
  return req(`rows/${table}`, jsonInit('POST', row))
}

/**
 * List rows of an allow-listed table. Pass `filters` to scope server-side by
 * exact column match (e.g. { camp_id }) — the Rust list_rows endpoint applies
 * them as a WHERE clause, so we never over-fetch other tenants' rows.
 */
export function apiList<T = Record<string, unknown>>(
  table: string,
  filters?: Record<string, string>,
): Promise<T[]> {
  const entries = Object.entries(filters ?? {}).filter(([, v]) => v != null && v !== '')
  const q = entries.length
    ? `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')}`
    : ''
  return req(`rows/${table}${q}`)
}

/** Fetch a single row by id, or null when not found. */
export async function apiGetById<T = Record<string, unknown>>(
  table: string,
  id: string,
): Promise<T | null> {
  try {
    return await req<T>(`rows/${table}/${encodeURIComponent(id)}`)
  } catch (err) {
    if (err instanceof Error && / 404 /.test(err.message)) return null
    throw err
  }
}

export function apiUpdate<T = Record<string, unknown>>(
  table: string,
  id: string,
  patch: unknown,
): Promise<T> {
  return req(`rows/${table}/${id}`, jsonInit('PATCH', patch))
}

export function apiDelete(table: string, id: string): Promise<void> {
  return req(`rows/${table}/${id}`, { method: 'DELETE' })
}
